import React, { useEffect, useRef, useState } from 'react';
import { track } from '@lf/analytics-events';
import { FunLoader } from '@lf/ui-primitives';
import SaveHeartButton from '../../components/SaveHeartButton';
import FeedbackButton from '../../components/FeedbackButton';
import { LoadingButton } from '@lf/ui-primitives';
import { useInViewOnce } from '../../lib/utils/useAnimateOnVisible';

function AnimatedBorder({ inView, radius = 16, strokeWidth = 2 }) {
    const ref = useRef(null);
    const [size, setSize] = useState({ w: 0, h: 0 });

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const update = () => {
            const rect = el.getBoundingClientRect();
            setSize({ w: rect.width, h: rect.height });
        };
        update();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const ready = size.w > 0 && size.h > 0;
    return (
        <svg
            ref={ref}
            aria-hidden="true"
            overflow="visible"
            className="absolute inset-0 w-full h-full pointer-events-none text-secondary-dark"
        >
            {ready && (
                <rect
                    x={strokeWidth}
                    y={strokeWidth}
                    width={Math.max(0, size.w - strokeWidth * 2)}
                    height={Math.max(0, size.h - strokeWidth * 2)}
                    rx={radius}
                    ry={radius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    pathLength={1}
                    strokeDasharray={1}
                    strokeDashoffset={inView ? 0 : 1}
                    style={{ transition: 'stroke-dashoffset 1100ms ease-out' }}
                />
            )}
        </svg>
    );
}

function SkeletonCard() {
    return (
        <div className="flex-col border rounded-2xl p-3 relative !bg-white animate-subtle-pulse">
            <div className="aspect-video rounded-xl bg-gray-100" />
            <div className="mt-4 space-y-2">
                <div className="h-4 bg-gray-100 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
        </div>
    );
}

function CompletionBadge({ inView }) {
    return (
        <div className="absolute top-2 right-2 z-10 bg-secondary-dark text-white rounded-full w-9 h-9 flex items-center justify-center shadow-md border-2 border-white">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline
                    points="4,10.5 8.5,15 16,6"
                    pathLength={1}
                    strokeDasharray={1}
                    strokeDashoffset={inView ? 0 : 1}
                    style={{ transition: 'stroke-dashoffset 500ms ease-out 80ms' }}
                />
            </svg>
        </div>
    );
}

function RecommendedVideoCard({
    video,
    position,
    isCompleted,
    quizNeeded,
    onVideoSelect,
    feedItems,
    isSaved,
    onSaveToggle,
}) {
    const videoId = video.video_id;
    const [cardRef, inView] = useInViewOnce({ threshold: 0 });

    const videoObj = {
        id: { videoId },
        snippet: {
            channelId: video.channel_id,
            title: video.title,
            thumbnails: { high: { url: video.thumbnail_url }, medium: { url: video.thumbnail_url }, default: { url: video.thumbnail_url } },
            channelTitle: '',
        },
    };

    const handleSelect = () => {
        track('rec_path_clicked', {
            lesson_id: feedItems?.current_position?.lesson_id || feedItems?.lesson_id,
            study_plan_id: feedItems?.study_plan_id,
            video_id: videoId,
            position,
        });
        onVideoSelect(
            videoObj,
            feedItems.study_plan_id,
            feedItems.current_position,
            { source: 'rec_path', recPosition: position },
        );
    };

    return (
        <div
            ref={cardRef}
            className={`flex-col rounded-2xl p-3 relative !bg-white ${isCompleted ? 'border-2 border-transparent' : 'border'}`}
        >
            {isCompleted && <AnimatedBorder inView={inView} />}
            <div className={`relative aspect-video rounded-xl overflow-hidden flex items-center justify-center ${isCompleted ? 'bg-white' : 'bg-black'}`}>
                {isCompleted && <CompletionBadge inView={inView} />}
                <img
                    src={video.thumbnail_url}
                    alt={video.title}
                    className={`w-full cursor-pointer object-fill ${isCompleted ? 'opacity-50 grayscale' : ''}`}
                    onClick={handleSelect}
                />
            </div>

            <div className="flex-1 mt-4">
                <h4
                    className={`cursor-pointer ${isCompleted ? 'font-medium text-gray-400' : 'font-bold'}`}
                    onClick={handleSelect}
                >
                    {video.title}
                </h4>
                {video.reason && (
                    <p className={`text-sm mt-1 ${isCompleted ? 'text-gray-400' : 'text-gray-500'}`}>
                        {video.reason}
                    </p>
                )}
                {quizNeeded && (
                    <button
                        className="mt-2 bg-tertiary text-white text-sm font-semibold px-4 py-2 rounded hover:bg-tertiary-dark"
                        onClick={handleSelect}
                    >
                        Take the quiz
                    </button>
                )}
            </div>

            <div className="absolute bottom-0 right-0 mb-2 mr-2 flex gap-1">
                <SaveHeartButton
                    isSaved={isSaved}
                    onClick={() => onSaveToggle(videoObj)}
                />
                <FeedbackButton
                    contentIds={[videoId, video.channel_id]}
                    data={{ lesson_id: feedItems.lesson_id }}
                />
            </div>
        </div>
    );
}

const RecommendedPath = ({
    recommendedPath,
    feedItems,
    isLastTopic = false,
    onVideoSelect,
    savedVideoIds,
    onSaveToggle,
    onNext,
    onNextTopic,
    isItemsLoading = false,
    isLoading = false,
}) => {
    const status = recommendedPath?.status;
    const recommended_videos = recommendedPath?.recommended_videos;
    const num_videos = recommendedPath?.num_videos;
    const lessonId = feedItems?.current_position?.lesson_id || feedItems?.lesson_id;
    const studyPlanId = feedItems?.study_plan_id;

    // Fire `rec_path_viewed` once per (lesson, study_plan) when the
    // recommended path becomes ready and renders. Mounted-once semantics
    // come from the dependency on lessonId+studyPlanId; navigation
    // between lessons re-arms the effect.
    useEffect(() => {
        if (status === 'ready' && lessonId && studyPlanId) {
            track('rec_path_viewed', {
                lesson_id: lessonId,
                study_plan_id: studyPlanId,
                position: 0,
            });
        }
    }, [status, lessonId, studyPlanId]);

    // Treat an in-flight first GET (no response yet) the same as a server-
    // reported pending/computing status. Without this, the child sees the
    // raw All Videos grid with no indication that recommendations are still
    // being computed (the GET is synchronous and can take 30-90s on a fresh
    // lesson — see VideosPage.jsx).
    const isComputing =
        status === 'computing'
        || status === 'pending'
        || (isLoading && !recommendedPath);

    if (!recommendedPath && !isComputing) {
        return null;
    }

    // Watched videos now stay in `recommended_videos` with `progress` >= 100.
    const completedVideoIds = new Set(
        (recommended_videos || [])
            .filter((rv) => (rv.progress || 0) >= 100)
            .map((rv) => rv.video_id),
    );

    // Display order: keep the server-defined position; the server returns
    // watched videos first, then newly-selected ones.
    const displayVideos = (status === 'ready' && recommended_videos) ? recommended_videos : [];

    const progressByVideoId = feedItems?.progress_by_video_id || {};

    // For computing state, show loading placeholders only.
    if (isComputing) {
        const skeletonCount = Math.max(1, num_videos || 4);

        return (
            <div className="my-8" data-testid="recommended-path-loading">
                <h3 className="text-2xl font-bold text-primary-dark">
                    Recommended
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                    Loading recommendations...
                </p>
                {!isItemsLoading ? (
                    <FunLoader
                        title="Picking your recommended videos..."
                        subtitle="Matching the best clips to this lesson"
                        ariaLabel="Loading recommendations"
                    />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 w-full">
                        {Array.from({ length: skeletonCount }, (_, i) => (
                            <SkeletonCard key={`skeleton-${i}`} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (status === 'error' || !recommended_videos || recommended_videos.length === 0) {
        return null;
    }

    // Topic-scoped counter — single-source per chairman's directive.
    //
    // Backend invariant (LF-100 strategy_d + recommended_path fix):
    // per_topic_remaining[topicId] counts uncompleted videos using the
    // SAME primary-first rule filterRecommendedPathByTopic uses on the
    // FE, so completedVideoIds.size + perTopicRemainingValue equals
    // displayVideos.length by construction. That keeps the counter
    // (numerator and denominator) and the rendered cards aligned —
    // even when bridge-concept videos make video_topic_map[v] legitimately
    // multi-topic.
    //
    // allDone derives directly from perTopicRemainingValue rather than
    // a >= compare, so it's reachable exactly when the backend says
    // there are no more uncompleted videos for the topic — which by
    // the invariant above means every rendered card is complete.
    const topicId = feedItems?.current_position?.topic_id;
    const per_topic_remaining = recommendedPath?.per_topic_remaining || {};
    const topicCompletedCount = completedVideoIds.size;
    const perTopicRemainingValue = topicId ? per_topic_remaining[topicId] : undefined;
    const hasBackendTopicSignal = perTopicRemainingValue !== undefined;
    const topicTotalCount = hasBackendTopicSignal
        ? perTopicRemainingValue + topicCompletedCount
        : displayVideos.length;
    const allDone = hasBackendTopicSignal
        ? (topicTotalCount > 0 && perTopicRemainingValue === 0)
        : (topicTotalCount > 0 && topicCompletedCount >= topicTotalCount);

    return (
        <div className="my-8">
            <h3 className="text-2xl font-bold text-primary-dark">
                Recommended
            </h3>
            <p className="text-sm text-gray-500 mb-3">
                {topicCompletedCount} of {topicTotalCount} completed
            </p>

            {allDone && feedItems?.study_plan_id && (onNextTopic || onNext) && !isLastTopic && (
                <div className="mb-4">
                    <LoadingButton
                        onClick={() => (onNextTopic
                            ? onNextTopic(feedItems.study_plan_id)
                            : onNext(feedItems.study_plan_id, true))}
                        text="Next topic"
                    />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 w-full">
                {displayVideos.map((video, index) => {
                    const progress = progressByVideoId[video.video_id] ?? video.progress;
                    const isVideoCompleted = completedVideoIds.has(video.video_id);
                    const quizNeeded = !isVideoCompleted && progress > 0 && progress < 100;

                    return (
                        <RecommendedVideoCard
                            key={video.video_id}
                            video={video}
                            position={index}
                            isCompleted={isVideoCompleted}
                            quizNeeded={quizNeeded}
                            onVideoSelect={onVideoSelect}
                            feedItems={feedItems}
                            isSaved={savedVideoIds.has(video.video_id)}
                            onSaveToggle={onSaveToggle}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default RecommendedPath;
